import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Info } from 'lucide-react';
import { getImageUrl } from '../config/runtime';

/**
 * FullscreenImageSpotlightViewer
 * Props:
 * - isOpen: boolean
 * - imageUrl: string
 * - spotlights: Array<{ id, title, entity, bounding_box | bounding_boxes }>
 * - onClose: function
 * - onOpenSpotlight: function(spotlight)
 */
const FullscreenImageSpotlightViewer = ({ isOpen, imageUrl, spotlights = [], onClose, onOpenSpotlight, highlightSpotlightId = null }) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [activeSpotlight, setActiveSpotlight] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isActive, setIsActive] = useState(false); // triggers enter animation
  const ANIMATION_MS = 300;

  // Mount flag for portal safety
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Helper to request close with exit animation
  const requestClose = useCallback(() => {
    if (isAnimatingOut) return;
    setIsActive(false);
    setIsAnimatingOut(true);
    setTimeout(() => {
      if (typeof onClose === 'function') onClose();
    }, ANIMATION_MS);
  }, [isAnimatingOut, onClose]);

  // Close on Esc
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, requestClose]);

  // Measure image natural and displayed sizes
  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    setNaturalSize({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    // Measure display size after layout pass
    requestAnimationFrame(() => {
      setDisplaySize({ width: img.clientWidth || 0, height: img.clientHeight || 0 });
    });
  }, []);

  // Recalculate on resize
  useEffect(() => {
    if (!isOpen) return;
    const img = imageRef.current;
    if (!img) return;
    const ro = new ResizeObserver(() => {
      setDisplaySize({ width: img.clientWidth || 0, height: img.clientHeight || 0 });
    });
    ro.observe(img);
    return () => ro.disconnect();
  }, [isOpen]);

  // Control visibility for enter/exit animations
  useEffect(() => {
    if (isOpen) {
      setIsAnimatingOut(false);
      setIsVisible(true);
      setIsActive(false);
      // ensure the element mounts with initial styles before activating transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsActive(true));
      });
    } else if (isVisible) {
      setIsActive(false);
      setIsAnimatingOut(true);
      const t = setTimeout(() => {
        setIsVisible(false);
        setIsAnimatingOut(false);
      }, ANIMATION_MS);
      return () => clearTimeout(t);
    }
  }, [isOpen, isVisible]);

  // Prevent background scroll while visible (includes during exit animation)
  useEffect(() => {
    if (!isVisible) return;
    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [isVisible]);

  // Auto-select highlighted spotlight when opening
  useEffect(() => {
    if (!isOpen || !highlightSpotlightId) return;
    const s = spotlights?.find(sp => sp?.id === highlightSpotlightId);
    if (s) setActiveSpotlight(s);
  }, [isOpen, highlightSpotlightId, spotlights]);

  // Flatten all bounding boxes per spotlight to a common list of markers
  const markers = useMemo(() => {
    if (!spotlights || spotlights.length === 0) return [];
    const results = [];
    for (const s of spotlights) {
      const makeMarker = (bbox, index = 0) => ({
        id: `${s.id || s.title}-${index}`,
        title: s.title || s.entity || 'Spotlight',
        spotlight: s,
        bbox
      });
      if (s?.bounding_boxes && Array.isArray(s.bounding_boxes)) {
        s.bounding_boxes.forEach((bbox, idx) => results.push(makeMarker(bbox, idx)));
      } else if (s?.bounding_box) {
        results.push(makeMarker(s.bounding_box, 0));
      }
    }
    return results;
  }, [spotlights]);

  // Convert pixel bbox -> positions relative to image display size with proper scaling
  const markerStyles = useMemo(() => {
    const { width: natW, height: natH } = naturalSize;
    const { width: dispW, height: dispH } = displaySize;
    if (natW === 0 || natH === 0 || dispW === 0 || dispH === 0) return [];
    
    // Calculate how the image is scaled within the container (object-contain)
    const scaleX = dispW / natW;
    const scaleY = dispH / natH;
    const scale = Math.min(scaleX, scaleY); // object-contain uses the smaller scale
    
    // Calculate the actual rendered image dimensions within the container
    const renderedWidth = natW * scale;
    const renderedHeight = natH * scale;
    
    // Calculate offsets to center the image within the container
    const offsetX = (dispW - renderedWidth) / 2;
    const offsetY = (dispH - renderedHeight) / 2;
    
    return markers.map(({ id, title, spotlight, bbox }, idx) => {
      const { x1, y1, x2, y2 } = bbox || {};
      if ([x1, y1, x2, y2].some(v => typeof v !== 'number')) return null;
      
      // Assign a distinct, stable color per marker index
      const hue = (idx * 57) % 360; // pseudo-random spread
      const color = `hsla(${hue}, 80%, 55%, 0.95)`;
      const fill = `hsla(${hue}, 80%, 55%, 0.18)`;
      // Debug: Log what we're working with
      console.log(`[FullscreenImageSpotlightViewer] ${title}:`, {
        bbox: `${x1},${y1} to ${x2},${y2}`,
        naturalSize: `${natW}x${natH}`,
        displaySize: `${dispW}x${dispH}`,
        scale: scale.toFixed(3),
        offset: `${offsetX.toFixed(1)},${offsetY.toFixed(1)}`
      });
      
      // Convert pixel coordinates to display coordinates with proper scaling
      const leftPx = offsetX + (x1 * scale);
      const topPx = offsetY + (y1 * scale);
      const widthPx = (x2 - x1) * scale;
      const heightPx = (y2 - y1) * scale;
      
      // Position label at top-left corner of bounding box
      const labelLeftPx = leftPx;
      const labelTopPx = topPx;
      
      // Clamp label within image bounds with small margin
      const marginPx = 2;
      const clamp = (val, min, max) => Math.min(Math.max(val, min + marginPx), Math.max(min + marginPx, max - marginPx));
      const clampedLabelLeft = clamp(labelLeftPx, offsetX, offsetX + renderedWidth);
      const clampedLabelTop = clamp(labelTopPx, offsetY, offsetY + renderedHeight);
      return {
        id,
        title,
        spotlight,
        color,
        fill,
        style: {
          position: 'absolute',
          left: `${clampedLabelLeft}px`,
          top: `${clampedLabelTop}px`,
          transform: 'translate(0, 0)'
        },
        bboxStyle: {
          position: 'absolute',
          left: `${leftPx}px`,
          top: `${topPx}px`,
          width: `${widthPx}px`,
          height: `${heightPx}px`,
          border: '2px solid',
          borderColor: color,
          backgroundColor: fill,
          borderRadius: '2px'
        },
      };
    }).filter(Boolean);
  }, [markers, naturalSize, displaySize]);

  if (!isVisible) return null;

  const content = (
    <div
      ref={containerRef}
      className={`fixed inset-0 z-[2000] bg-black/90 backdrop-blur-sm w-[100vw] h-[100dvh] overscroll-none overflow-hidden will-change-transform will-change-opacity transition-transform transition-opacity duration-300 ease-out ${
        isActive && !isAnimatingOut ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'
      }`}
      role="dialog"
      aria-modal="true"
      onClick={requestClose}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 h-14 flex items-center justify-between px-3" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={requestClose}
          className="p-2 rounded-md text-white/80 hover:text-white"
          aria-label="Close viewer"
        >
          <X size={22} />
        </button>
      </div>

      {/* Image + markers */}
      <div className="w-full h-full flex items-start md:items-center justify-center p-4 pt-20 md:pt-4">
        {/* Wrapper sizes itself exactly to the rendered image via inline-block */}
        <div className="relative inline-block group" onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
          {/* Image */}
          <img
            ref={imageRef}
            src={getImageUrl(imageUrl)}
            alt="Fullscreen"
            className="block max-w-[100vw] md:max-w-[90vw] max-h-[calc(100dvh-180px)] md:max-h-[80vh] object-contain select-none"
            draggable="false"
            onLoad={handleImageLoad}
          />

          {/* Markers and bounding rectangles */}
          <div className="pointer-events-none absolute inset-0">
            {markerStyles.map(m => (
              <React.Fragment key={m.id}>
                {/* BBox outline - show only for active/highlighted spotlight */}
                {(activeSpotlight?.id === m.spotlight?.id || m.spotlight?.id === highlightSpotlightId) && (
                  <div
                    className="md:block transition-opacity duration-150 opacity-100"
                    style={m.bboxStyle}
                  />
                )}
                {/* Marker button */}
                <button
                  className={"pointer-events-auto absolute text-black font-medium text-[11px] md:text-xs px-2 py-1 rounded-md shadow border border-white/20"}
                  style={{
                    ...m.style,
                    backgroundColor: m.spotlight?.id === highlightSpotlightId ? m.color : m.color,
                    outline: m.spotlight?.id === highlightSpotlightId ? `2px solid ${m.fill}` : 'none',
                    boxShadow: `0 0 0 2px rgba(0,0,0,0.1)`
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSpotlight(m.spotlight);
                    if (typeof onOpenSpotlight === 'function') onOpenSpotlight(m.spotlight);
                  }}
                >
                  <span className="inline-flex items-center gap-1">
                    <Info size={12} />
                    {m.title}
                  </span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Spotlight overlay panel */}
      {activeSpotlight && (
        <div className="absolute bottom-0 left-0 right-0 p-3" onClick={(e) => e.stopPropagation()}>
          <div className="mx-auto max-w-3xl bg-white/95 text-gray-900 rounded-xl shadow-2xl border border-gray-200 max-h-[45vh] overflow-hidden flex flex-col pb-[calc(env(safe-area-inset-bottom,0)+0px)]">
            <div className="flex items-start justify-between p-3">
              <div className="pr-3">
                <div className="text-sm font-semibold truncate">{activeSpotlight.title || activeSpotlight.entity}</div>
                {activeSpotlight.subtitle && (
                  <div className="text-xs text-gray-600 mt-0.5 truncate">{activeSpotlight.subtitle}</div>
                )}
              </div>
              <button
                className="p-1 text-gray-500 hover:text-gray-800"
                onClick={() => setActiveSpotlight(null)}
                aria-label="Close spotlight details"
              >
                <X size={16} />
              </button>
            </div>
            {activeSpotlight.show_more_content && (
              <div className="px-3 pb-3 text-sm leading-relaxed overflow-y-auto">
                {activeSpotlight.show_more_content}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return mounted && typeof document !== 'undefined' ? createPortal(content, document.body) : null;
};

export default FullscreenImageSpotlightViewer;


